import React, { useEffect, useState } from 'react';
import { Plus, Save, RotateCcw, Loader2 } from 'lucide-react';
import { lunchMenu } from '../../data/menuData';
import { useToast } from '../../hooks/use-toast';
import DishRow from './DishRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = [['monday', 'MA'], ['tuesday', 'TI'], ['wednesday', 'KE'], ['thursday', 'TO'], ['friday', 'PE'], ['weekend', 'LA']];
const clone = (o) => JSON.parse(JSON.stringify(o));
const inputCls = 'w-full bg-white text-gray-900 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500';

const LunchMenuEditor = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('adminToken');
  const [menus, setMenus] = useState(null);
  const [day, setDay] = useState('monday');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetch(`${API}/lunch-menu`).then((r) => r.json()).then((d) => {
      setMenus(clone(d.dailyMenus || lunchMenu.dailyMenus));
      setUpdatedAt(d.updated_at);
    });
  }, []);

  if (!menus) return <div className="p-8 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Ladataan…</div>;
  const menu = menus[day];

  const setDayField = (patch) => setMenus((m) => ({ ...m, [day]: { ...m[day], ...patch } }));
  const setList = (list, fn) => setMenus((m) => ({ ...m, [day]: { ...m[day], [list]: fn([...(m[day][list] || [])]) } }));
  const updateItem = (list, i, patch) => setList(list, (arr) => { arr[i] = { ...arr[i], ...patch }; return arr; });
  const moveItem = (list, i, dir) => setList(list, (arr) => { [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]]; return arr; });
  const deleteItem = (list, i) => setList(list, (arr) => arr.filter((_, idx) => idx !== i));
  const addItem = (list) => setList(list, (arr) => [...arr, { name: '', nameFi: '', nameSv: '', dietary: [] }]);

  const translate = async (list, i) => {
    const d = menu[list][i];
    const name = d.name || d.nameFi;
    if (!name) { toast({ title: 'Kirjoita ensin ruoan nimi', variant: 'destructive' }); return; }
    setTranslating(`${list}-${i}`);
    try {
      const r = await fetch(`${API}/admin/translate-dish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, description: d.description || d.descriptionFi || '' }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Käännös epäonnistui');
      const t = await r.json();
      const hasDesc = !!(d.description || d.descriptionFi);
      const patch = list === 'mains' && hasDesc ? t : { name: t.name, nameFi: t.nameFi, nameSv: t.nameSv };
      updateItem(list, i, patch);
      toast({ title: 'Käännetty – tarkista tekstit' });
    } catch (e) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setTranslating(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/lunch-menu`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dailyMenus: menus }),
      });
      if (!r.ok) throw new Error('Tallennus epäonnistui');
      setUpdatedAt((await r.json()).updated_at);
      toast({ title: 'Lounaslista tallennettu ja julkaistu' });
    } catch (e) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Palautetaanko oletuslounaslista? Tallentamattomat muutokset menetetään.')) return;
    await fetch(`${API}/admin/lunch-menu`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setMenus(clone(lunchMenu.dailyMenus));
    setUpdatedAt(null);
    toast({ title: 'Oletuslista palautettu' });
  };

  const renderList = (list, compact) => (menu[list] || []).map((dish, i, arr) => (
    <DishRow key={`${day}-${list}-${i}`} dish={dish} index={i} total={arr.length} compact={compact}
      translating={translating === `${list}-${i}`} testPrefix={`lunch-${list}-${i}`}
      onChange={(p) => updateItem(list, i, p)} onMove={(dir) => moveItem(list, i, dir)}
      onDelete={() => deleteItem(list, i)} onTranslate={() => translate(list, i)} />
  ));

  return (
    <div className="space-y-6" data-testid="lunch-menu-editor">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="text-2xl font-light text-gray-900">Viikon lounaslista</h2>
          <p className="text-sm text-gray-500">{updatedAt ? `Viimeksi tallennettu ${new Date(updatedAt).toLocaleString('fi-FI')}` : 'Käytössä oletuslista (menuData)'}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-gray-300 text-sm" data-testid="lunch-reset-btn"><RotateCcw className="w-4 h-4" /> Palauta oletus</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold disabled:opacity-60" data-testid="lunch-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Tallenna & julkaise
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        {DAYS.map(([k, l]) => (
          <button key={k} onClick={() => setDay(k)} data-testid={`lunch-day-tab-${k}`}
            className={`px-4 py-2 rounded-md text-sm font-bold ${day === k ? 'bg-gray-900 text-white' : 'bg-white border border-gray-300 text-gray-600'}`}>{l}</button>
        ))}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid md:grid-cols-4 gap-3">
        {[['day', 'Päivän teema EN'], ['dayFi', 'Päivän teema FI'], ['daySv', 'Päivän teema SV'], ['hours', 'Aukioloaika']].map(([f, l]) => (
          <label key={f} className="text-xs text-gray-500">{l}
            <input className={inputCls} value={menu[f] || ''} onChange={(e) => setDayField({ [f]: e.target.value })} data-testid={`lunch-day-${f}`} />
          </label>
        ))}
      </div>

      <section className="space-y-2">
        <h3 className="font-semibold text-gray-800">Pääruoat</h3>
        {renderList('mains', false)}
        <button onClick={() => addItem('mains')} className="inline-flex items-center gap-1.5 text-sm text-amber-700 font-semibold" data-testid="lunch-add-main-btn"><Plus className="w-4 h-4" /> Lisää ruoka</button>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-gray-800">Lisukkeet & juomat</h3>
        {renderList('sides', true)}
        <button onClick={() => addItem('sides')} className="inline-flex items-center gap-1.5 text-sm text-amber-700 font-semibold" data-testid="lunch-add-side-btn"><Plus className="w-4 h-4" /> Lisää lisuke</button>
      </section>
    </div>
  );
};

export default LunchMenuEditor;
